import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  DatePicker,
  Grid,
  Input,
  Modal,
  Segmented,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip as AntTooltip,
  Spin,
  message,
} from 'antd';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import withPrivateRoute from '@/components/hoc/withPrivateRoute';
import { ContentBackground } from '@/components/content';
import { ContainerFluid, Section } from '@/components/layouts';
import RootLayout from '@/components/layouts/RootLayout';
import HolidayHistoryView from '@/components/holiday-history/HolidayHistoryView';
import OtherLeavesHistoryView from '@/components/other-leaves-history/OtherLeavesHistoryView';
import { OPERATION, ROLES, ROLES_PERMISSIONS, ROUTES } from '@/constants';
import { MONTHS_OPTIONS } from '@/shared/constants';
import { API } from '@/services/api.service';

const toHex = (argb, fallback) => {
  if (!argb || typeof argb !== 'string') return fallback;
  const rgb = argb.length === 8 ? argb.slice(2) : argb;
  return `#${rgb}`;
};

// 'YYYY-MM-DD' -> 'DD.MM.YYYY' for compact period display.
const fmtDay = (s) => {
  if (!s || typeof s !== 'string') return '';
  const [y, m, d] = s.split('-');
  return d ? `${d}.${m}.${y}` : s;
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const { RangePicker } = DatePicker;

function LeavesOverview() {
  const { t } = useTranslation(['translations', 'tables', 'forms', 'menus']);
  const lo = (key) => t(`leavesOverview.${key}`, { ns: 'translations' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(searchParams.get('company') || undefined);
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [types, setTypes] = useState([]);
  const [view, setView] = useState('matrix');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [exporting, setExporting] = useState(false);
  const [singleCompany, setSingleCompany] = useState(false);
  // Row-click opens a large modal with the per-employee leave history
  // (annual + other leaves). `null` = closed.
  const [detailEmp, setDetailEmp] = useState(null);

  // The matrix (many type columns) is unusable on phones — force the list view
  // there and hide the matrix/list toggle.
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const effectiveView = isMobile ? 'list' : view;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await API.companies.retrieveDropdownAllCompanies(null);
        const list = Array.isArray(res)
          ? res
          : res?.items || res?.companies || res?.data || [];
        if (!active) return;
        setCompanies(list);
        // Single-company users: preselect it and drop the "All companies" option.
        const single = res?.isSingleCompany || list.length === 1;
        setSingleCompany(single);
        if (single && list[0]) setCompany(list[0]._id);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filters = useMemo(() => {
    const hasRange =
      Array.isArray(dateRange) && dateRange[0] && dateRange[1];
    return {
      company: company || 'all',
      search: search || undefined,
      types: types.length ? types : undefined,
      // A date range takes priority over year/month.
      ...(hasRange
        ? {
            dateFrom: dateRange[0].format('YYYY-MM-DD'),
            dateTo: dateRange[1].format('YYYY-MM-DD'),
          }
        : { year, month }),
    };
  }, [company, search, year, month, dateRange, types]);

  // Reset to first page whenever a filter or the view changes.
  useEffect(() => {
    setPage(1);
  }, [company, search, year, month, dateRange, types, view]);

  const { data, isFetching } = useQuery({
    queryKey: ['leaves-overview', filters, effectiveView, page, limit],
    queryFn: () =>
      API.employees.getLeavesOverview({
        ...filters,
        view: effectiveView,
        page,
        limit,
      }),
    keepPreviousData: true,
  });

  const chartTypes = data?.types || [];
  const typeOptions = useMemo(
    () => chartTypes.map((tp) => ({ value: tp.key, label: tp.label })),
    [chartTypes]
  );

  const companyOptions = useMemo(() => {
    const opts = companies.map((c) => ({ value: c._id, label: c.name }));
    return singleCompany
      ? opts
      : [{ value: 'all', label: lo('allCompanies') }, ...opts];
  }, [companies, singleCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  // Human-readable label of the active period (range wins over year/month),
  // reused in the per-employee detail summary.
  const periodLabel = useMemo(() => {
    const hasRange = Array.isArray(dateRange) && dateRange[0] && dateRange[1];
    if (hasRange) {
      return `${dateRange[0].format('DD.MM.YYYY')} – ${dateRange[1].format(
        'DD.MM.YYYY'
      )}`;
    }
    return month ? `${year}, ${month}` : String(year);
  }, [dateRange, year, month]);

  // The clicked employee's matrix row (per-type day counts) — only the matrix
  // view aggregates per employee; the list view rows are individual spells.
  const detailRow = useMemo(
    () =>
      effectiveView === 'matrix'
        ? (data?.items || []).find((r) => r.employeeId === detailEmp?.id)
        : null,
    [data, detailEmp, effectiveView]
  );

  const matrixColumns = useMemo(() => {
    const cols = [
      {
        title: lo('colEmployee'),
        dataIndex: 'employeeName',
        key: 'employeeName',
        width: 220,
      },
      ...chartTypes.map((tp) => {
        // The period breakdown-on-hover is only meaningful for annual leave
        // (that is where the "days taken vs. balance / year split" confusion is).
        const isHoliday = tp.key === 'holidayLeave';
        return {
          title: isHoliday ? (
            <AntTooltip title={lo('colDaysTakenHint')}>
              <span>
                {tp.label}{' '}
                <InfoCircleOutlined style={{ opacity: 0.5, fontSize: 11 }} />
              </span>
            </AntTooltip>
          ) : (
            tp.label
          ),
          key: tp.key,
          width: 130,
          align: 'center',
          render: (_, row) => {
            const count = row.perType?.[tp.key] || 0;
            if (!isHoliday || !count) return count;
            const periods = row.periods?.[tp.key] || [];
            if (!periods.length) return count;
            return (
              <AntTooltip
                title={
                  <div>
                    <div style={{ marginBottom: 4, fontWeight: 600 }}>
                      {lo('detailUsedInPeriod')}: {periodLabel}
                    </div>
                    {periods.map((p) => (
                      <div key={`${p.dateFrom}-${p.dateTo}`}>
                        {fmtDay(p.dateFrom)} – {fmtDay(p.dateTo)}:{' '}
                        <strong>{p.days}</strong>
                      </div>
                    ))}
                    <div
                      style={{
                        marginTop: 4,
                        borderTop: '1px solid rgba(255,255,255,0.25)',
                        paddingTop: 4,
                      }}
                    >
                      {lo('colTotal')}: <strong>{count}</strong>
                    </div>
                  </div>
                }
              >
                <span style={{ borderBottom: '1px dotted', cursor: 'help' }}>
                  {count}
                </span>
              </AntTooltip>
            );
          },
        };
      }),
      {
        title: lo('colTotal'),
        dataIndex: 'total',
        key: 'total',
        width: 90,
        align: 'center',
        render: (v) => <strong>{v}</strong>,
      },
    ];
    return cols;
  }, [chartTypes, periodLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const listColumns = useMemo(
    () => [
      {
        title: lo('colEmployee'),
        dataIndex: 'employeeName',
        key: 'employeeName',
        width: 220,
      },
      {
        title: lo('colType'),
        dataIndex: 'typeLabel',
        key: 'type',
        width: 220,
        render: (label, row) => {
          const bg = toHex(row.color);
          return (
            <Tag
              style={
                bg
                  ? {
                      backgroundColor: bg,
                      color: toHex(row.textColor) || '#000',
                      border: 'none',
                    }
                  : undefined
              }
            >
              {label}
            </Tag>
          );
        },
      },
      {
        title: lo('colPeriod'),
        key: 'period',
        width: 200,
        render: (_, row) =>
          `${row.dateFrom}${row.dateTo && row.dateTo !== row.dateFrom ? ` – ${row.dateTo}` : ''}`,
      },
      {
        title: lo('colDays'),
        dataIndex: 'days',
        key: 'days',
        width: 90,
        align: 'center',
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleReset = () => {
    setCompany(singleCompany && companies[0] ? companies[0]._id : 'all');
    setSearch('');
    setYear(CURRENT_YEAR);
    setMonth(undefined);
    setDateRange(null);
    setTypes([]);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await API.employees.exportLeavesOverview(filters);
    } catch {
      message.error(lo('exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <RootLayout metadata={{ title: lo('title') }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />
      <Section>
        <ContainerFluid className="foreground-wrapper">
          <div className="min-w-0 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Button
                  icon={<ArrowLeftOutlined />}
                  className="btn-default btn-default--hover h-8"
                  onClick={() => navigate(`${ROUTES.admin}/${ROUTES.employees}`)}
                >
                  {t('back', { ns: 'menus' })}
                </Button>
                <h2 className="m-0 text-xl font-bold">{lo('title')}</h2>
              </div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                className="btn-primary h-8"
                loading={exporting}
                onClick={handleExport}
              >
                {lo('exportExcel')}
              </Button>
            </div>

            <Card size="small" className="shadow-sm">
              <div className="flex items-end gap-3 flex-wrap">
                <Field label={lo('company')}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    value={company || (singleCompany ? undefined : 'all')}
                    onChange={setCompany}
                    options={companyOptions}
                    disabled={singleCompany}
                    popupMatchSelectWidth={false}
                    style={{ width: 240 }}
                  />
                </Field>
                <Field label={lo('search')}>
                  <Input
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={lo('searchPlaceholder')}
                    style={{ width: 200 }}
                  />
                </Field>
                <Field label={lo('year')}>
                  <Select
                    value={year}
                    onChange={setYear}
                    disabled={!!(dateRange && dateRange[0] && dateRange[1])}
                    options={YEARS.map((y) => ({ value: y, label: y }))}
                    style={{ width: 110 }}
                  />
                </Field>
                <Field label={lo('month')}>
                  <Select
                    allowClear
                    value={month}
                    onChange={setMonth}
                    disabled={!!(dateRange && dateRange[0] && dateRange[1])}
                    placeholder={lo('allMonths')}
                    options={MONTHS_OPTIONS.map((m) => ({
                      value: MONTHS[m.value - 1],
                      label: t(m.key, { ns: 'forms' }),
                    }))}
                    style={{ width: 150 }}
                  />
                </Field>
                <Field label={lo('dateRange')}>
                  <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="DD.MM.YYYY"
                    allowClear
                  />
                </Field>
                <Field label={lo('type')}>
                  <Select
                    mode="multiple"
                    allowClear
                    value={types}
                    onChange={setTypes}
                    options={typeOptions}
                    placeholder={lo('allTypes')}
                    maxTagCount="responsive"
                    popupMatchSelectWidth={false}
                    style={{ minWidth: 240 }}
                  />
                </Field>
                {!isMobile && (
                  <Field label={lo('view')}>
                    <Segmented
                      value={view}
                      onChange={setView}
                      options={[
                        { value: 'matrix', label: lo('viewMatrix') },
                        { value: 'list', label: lo('viewList') },
                      ]}
                    />
                  </Field>
                )}
                <Button
                  className="btn-default btn-default--hover h-8 self-end"
                  onClick={handleReset}
                >
                  {t('clearAllFilters', { ns: 'tables' })}
                </Button>
              </div>
            </Card>

            <Spin spinning={isFetching}>
              {chartTypes.length > 0 && (
                <>
                <div className="grid min-w-0 gap-4 lg:grid-cols-2">
                  <Card
                    size="small"
                    title={lo('byTypeTitle')}
                    className="min-w-0 shadow-sm"
                  >
                    <svg width="0" height="0" className="absolute">
                      <defs>
                        {[
                          ...new Set(
                            (data?.byType || []).map((e) =>
                              toHex(e.color, '#1677ff')
                            )
                          ),
                        ].map((c) => (
                          <pattern
                            key={c}
                            id={`str-${c.replace('#', '')}`}
                            width="6"
                            height="6"
                            patternUnits="userSpaceOnUse"
                            patternTransform="rotate(45)"
                          >
                            <rect width="6" height="6" fill={c} />
                            <rect
                              width="3"
                              height="6"
                              fill="#ffffff"
                              fillOpacity="0.55"
                            />
                          </pattern>
                        ))}
                      </defs>
                    </svg>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={data?.byType || []}
                        margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={false} height={6} />
                        <YAxis
                          type="number"
                          scale="log"
                          domain={[1, 'dataMax']}
                          allowDataOverflow
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip
                          wrapperStyle={{ zIndex: 1000 }}
                          allowEscapeViewBox={{ x: false, y: true }}
                        />
                        <Bar
                          dataKey="totalDays"
                          name={lo('seriesDays')}
                          maxBarSize={26}
                        >
                          {(data?.byType || []).map((e) => (
                            <Cell key={e.key} fill={toHex(e.color, '#1677ff')} />
                          ))}
                        </Bar>
                        <Bar
                          dataKey="count"
                          name={lo('seriesPeriods')}
                          maxBarSize={26}
                        >
                          {(data?.byType || []).map((e) => (
                            <Cell
                              key={e.key}
                              fill={`url(#str-${toHex(e.color, '#1677ff').replace(
                                '#',
                                ''
                              )})`}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>

                  <Card
                    size="small"
                    title={lo('seasonalityTitle')}
                    className="min-w-0 shadow-sm"
                  >
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={data?.byMonth || []}
                        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="monthLabel"
                          height={22}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          allowDecimals={false}
                          tickCount={9}
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip
                          wrapperStyle={{ zIndex: 1000 }}
                          allowEscapeViewBox={{ x: false, y: true }}
                        />
                        {chartTypes.map((tp) => (
                          <Bar
                            key={tp.key}
                            dataKey={tp.key}
                            name={tp.label}
                            stackId="m"
                            fill={toHex(tp.color, '#52c41a')}
                            maxBarSize={44}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-2 text-xs">
                  <LegendSwatch color="#555" label={lo('seriesDays')} />
                  <LegendSwatch color="#555" striped label={lo('seriesPeriods')} />
                  <span className="mx-1 text-zinc-300">|</span>
                  {chartTypes.map((tp) => (
                    <LegendSwatch
                      key={tp.key}
                      color={toHex(tp.color, '#52c41a')}
                      label={tp.label}
                    />
                  ))}
                </div>
                </>
              )}

              <Card size="small" className="mt-4 min-w-0 overflow-hidden shadow-sm">
                <Table
                  rowKey={effectiveView === 'list' ? 'id' : 'employeeId'}
                  columns={
                    effectiveView === 'list' ? listColumns : matrixColumns
                  }
                  dataSource={data?.items || []}
                  size="small"
                  scroll={{ x: 'max-content' }}
                  onRow={(row) => ({
                    onClick: () => {
                      if (!row?.employeeId) return;
                      setDetailEmp({
                        id: row.employeeId,
                        name: row.employeeName,
                      });
                    },
                    style: { cursor: 'pointer' },
                  })}
                  pagination={{
                    current: page,
                    pageSize: limit,
                    total: data?.count || 0,
                    showSizeChanger: true,
                    onChange: (p, ps) => {
                      setPage(p);
                      setLimit(ps);
                    },
                  }}
                />
              </Card>
            </Spin>
          </div>
        </ContainerFluid>
      </Section>

      <Modal
        open={!!detailEmp}
        onCancel={() => setDetailEmp(null)}
        title={
          detailEmp ? (
            <Link
              to={`${ROUTES.admin}/${ROUTES.employees}/${OPERATION.view}/${detailEmp.id}`}
              className="inline-flex items-center gap-1 font-semibold hover:opacity-75"
            >
              {detailEmp.name}
              <LinkOutlined className="text-xs" />
            </Link>
          ) : null
        }
        width="90%"
        style={{ top: 24, maxWidth: 1400 }}
        styles={{ body: { maxHeight: '78vh', overflowY: 'auto' } }}
        footer={null}
        destroyOnClose
      >
        {detailEmp && (
          <div
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              background: 'rgba(0,0,0,0.03)',
              borderRadius: 8,
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <strong>
                {lo('detailUsedInPeriod')}: {periodLabel}
              </strong>
            </div>
            {detailRow ? (
              <div>
                {chartTypes.map((tp) =>
                  detailRow.perType?.[tp.key] ? (
                    <Tag key={tp.key} style={{ marginBottom: 4 }}>
                      {tp.label}: <strong>{detailRow.perType[tp.key]}</strong>
                    </Tag>
                  ) : null
                )}
                <Tag color="blue" style={{ marginBottom: 4 }}>
                  {lo('colTotal')}: <strong>{detailRow.total}</strong>
                </Tag>
              </div>
            ) : null}
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              {lo('detailUsedNote')}
            </div>
          </div>
        )}

        {detailEmp && (
          <Tabs
            defaultActiveKey="annual"
            items={[
              {
                key: 'annual',
                label: lo('tabAnnual'),
                children: <HolidayHistoryView employeeId={detailEmp.id} />,
              },
              {
                key: 'other',
                label: lo('tabOther'),
                children: <OtherLeavesHistoryView employeeId={detailEmp.id} />,
              },
            ]}
          />
        )}
      </Modal>
    </RootLayout>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-zinc-500">{label}</span>
      {children}
    </div>
  );
}

function LegendSwatch({ color, label, striped }) {
  const c = color || '#999';
  const pid = `lg-str-${c.replace('#', '')}`;
  return (
    <span className="inline-flex items-center gap-1">
      {striped ? (
        // Same pattern definition as the chart bars (rotate(45), 6px, white 0.55)
        // so the hatch direction/spacing matches exactly.
        <svg width="12" height="12" className="rounded-sm">
          <defs>
            <pattern
              id={pid}
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill={c} />
              <rect width="3" height="6" fill="#ffffff" fillOpacity="0.55" />
            </pattern>
          </defs>
          <rect width="12" height="12" rx="2" fill={`url(#${pid})`} />
        </svg>
      ) : (
        <span
          className="inline-block h-3 w-3 rounded-sm"
          style={{ backgroundColor: c }}
        />
      )}
      <span className="text-zinc-600">{label}</span>
    </span>
  );
}

// Manager-only access (same as the employee list, minus plain `employee`) so it
// matches the API route roles — an employee has their own per-profile history.
const LEAVES_OVERVIEW_ACCESS = {
  ...ROLES_PERMISSIONS.EMPLOYEES.VIEW,
  requiredRoles: ROLES_PERMISSIONS.EMPLOYEES.VIEW.requiredRoles.filter(
    (r) => r !== ROLES.employee
  ),
};

export default withPrivateRoute(LeavesOverview, LEAVES_OVERVIEW_ACCESS);
